"use client";
import useTheme from "../../hooks/useTheme";
import { isDarkTheme } from "../../../theme";

import RichTextDisplay from "../../common/rich-text/RichTextDisplay";
import { copyRichText } from "../../common/rich-text/copyRichText";
import { isRichText, richTextToPlain } from "../../common/rich-text/richText";

import { memo, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import useDownloadFile from "../../hooks/useDownloadFile";
import EmojiPicker, { Theme } from "emoji-picker-react";
import {
  BiCheck,
  BiCheckDouble,
  BiFile,
  BiDownload,
  BiReply,
  BiChevronDown,
  BiChevronUp,
  BiInfoCircle,
  BiX,
  BiTrash,
  BiSmile,
  BiError,
  BiTime,
  BiRevision,
  BiEdit,
  BiCopy,
} from "react-icons/bi";
import { callApi } from "../../common/Apicall";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { setChatModalOpen, updateMessage } from "../../Slices/ChatSlice";
import ParseMentions from "../../common/ParseMentions";
import ReplaceUrlsWithLinks from "../../common/ReplaceUrlsWithLinks";
import ReplaceAsTaskNotification from "../../common/ReplaceAsTaskNotification";
import { useNavigate } from "react-router-dom";
import { buildUrl } from "../../common/navigation";
import useSendMessage from "../../hooks/useSendMessage";
import FilePreviewModal from "./FilePreviewModal";

// Emoji picker geometry, matching the values the picker is rendered with
// below. Same approach as SoftwareCommentsCard.js, which already renders this
// picker in a portal for the identical reason (see the comment on
// getEmojiPickerPosition).
const REACTION_PICKER_WIDTH = 300;
const REACTION_PICKER_BODY_HEIGHT = 350;
// With `reactionsDefaultOpen` the picker first appears as just the compact
// quick-reaction strip and only grows to its full height if the user expands
// it, so this — not the full height — is what to position against initially.
const REACTION_PICKER_INITIAL_HEIGHT = 52;
const REACTION_PICKER_GAP = 8;

// Places the picker next to its trigger button in viewport coordinates,
// preferring above and falling back to below, clamped so it always stays
// fully on screen.
//
// `alignRight` mirrors the side the message sits on, matching how the picker
// was anchored before it moved into a portal: own messages hang it off the
// right edge (opening leftwards), received messages off the left edge
// (opening rightwards). Anchoring everything to the right — as the
// single-column comments version of this helper does — pushed the picker a
// full width away from received messages.
const getEmojiPickerPosition = (
  buttonRect,
  alignRight,
  pickerHeight = REACTION_PICKER_INITIAL_HEIGHT,
) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const preferredLeft = alignRight
    ? buttonRect.right - REACTION_PICKER_WIDTH
    : buttonRect.left;

  const left = Math.max(
    REACTION_PICKER_GAP,
    Math.min(
      preferredLeft,
      viewportWidth - REACTION_PICKER_WIDTH - REACTION_PICKER_GAP,
    ),
  );

  const showAbove = buttonRect.top - pickerHeight - REACTION_PICKER_GAP >= 0;
  const top = showAbove
    ? buttonRect.top - pickerHeight - REACTION_PICKER_GAP
    : Math.max(
        REACTION_PICKER_GAP,
        Math.min(
          buttonRect.bottom + REACTION_PICKER_GAP,
          viewportHeight - pickerHeight - REACTION_PICKER_GAP,
        ),
      );

  return { top, left };
};

const Message = ({ message, loading, setReplyingTo, setEditingMessage }) => {
  const theme = useTheme();
  const { sendmessage } = useSendMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_URL;
  const connection = useSelector((state) => state.socket.socketConnection);
  // Select only the two fields this component actually uses, not the whole
  // `conversation` slice. Selecting the slice object meant every message
  // bubble re-rendered whenever *any* part of it changed — including the
  // `messages` array itself, so reacting to one message re-rendered every
  // message in the thread. These two keep their reference across message
  // updates, so bubbles now only re-render when their own data changes.
  const selectedConversationType = useSelector(
    (state) => state.conversation.conversationType,
  );
  const selectedConvo = useSelector(
    (state) => state.conversation.selectedConversation,
  );
  const selectedConvoParticipants = selectedConvo?.participants;

  const { downloadFile, isLoading } = useDownloadFile();
  const currentUser = useSelector(
    (state) => state?.data?.userInfo?.data?.data?.UserId,
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const [showSeenBy, setShowSeenBy] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [popupPosition, setPopupPosition] = useState("top");
  // Viewport coordinates for the portalled emoji picker (null = not open).
  const [emojiPickerPosition, setEmojiPickerPosition] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [reactionPop, setReactionPop] = useState(null);
  const seenByRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const messageRef = useRef(null);
  const reactionPopTimerRef = useRef(null);

  const fromMe = message.senderId === currentUser;
  const formattedTime = moment.utc(message.createdAt).local().format("hh:mm A");
  const MESSAGE_LENGTH_THRESHOLD = 500;
  const formatReadTime = (timestamp) => {
    const now = moment();
    const readTime = moment.utc(timestamp).local();
    const diffMinutes = now.diff(readTime, "minutes");
    const diffHours = now.diff(readTime, "hours");

    if (diffMinutes < 1) {
      return "just now";
    } else if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    } else if (diffHours < 24) {
      return `Today at ${readTime.format("h:mm A")}`;
    } else if (diffHours < 48) {
      return `Yesterday at ${readTime.format("h:mm A")}`;
    } else if (diffHours < 168) {
      // Less than 7 days
      return readTime.format("ddd, h:mm A");
    } else {
      return readTime.format("MMM D, h:mm A");
    }
  };

  const canEdit = () => {
    if (!fromMe) return false;
    const messageTime = moment.utc(message.createdAt);
    const now = moment.utc();
    const hoursDiff = now.diff(messageTime, "hours");
    return hoursDiff < 2;
  };

  const handleEditClick = () => {
    if (canEdit()) {
      setEditingMessage(message);
    }
  };

  // Initialize reactions state from message
  const [reactions, setReactions] = useState(message.reactions || {});
  const trigger = useCallback(
    (taskId, ticketType) => {
      dispatch(setChatModalOpen(false));

      let screenName = "SearchedTicket";
      // You can customize the screen based on ticket type if needed
      if (ticketType === "Scrum Ticket") {
        screenName = "SoftwareTickets";
      } else if (ticketType === "Support Ticket") {
        screenName = "SupportTickets";
      }

      navigate(buildUrl(screenName, { taskId }));
    },
    [dispatch, navigate],
  );

  const renderContent = () => {
    const content = message.content;
    if (!content) return null;

    // Updated regex to handle all three ticket types
    const taskRegex =
      /\[\[You have been assigned a new (Internal Task|Scrum Ticket|Support Ticket) ticket: ([\s\S]+?) : (\d+)\]\]/;
    const isTaskNotification = taskRegex.test(content);

    if (isTaskNotification) {
      return <ReplaceAsTaskNotification content={content} trigger={trigger} />;
    }

    if (isRichText(content)) {
      return <RichTextDisplay value={content}
        className={!isExpanded && richTextToPlain(content).length > MESSAGE_LENGTH_THRESHOLD ? "line-clamp-6" : ""}
        renderText={(text, insideLink) => insideLink ? text : ReplaceUrlsWithLinks(ParseMentions(text, fromMe))} />;
    }

    // Legacy plain-text messages retain their automatic links.
    const contentWithMentions = ParseMentions(content, fromMe);
    const contentWithLinks = ReplaceUrlsWithLinks(contentWithMentions);

    if (content?.length <= MESSAGE_LENGTH_THRESHOLD) {
      return contentWithLinks;
    }

    if (isExpanded) {
      return contentWithLinks;
    } else {
      const truncatedContent = content.slice(0, MESSAGE_LENGTH_THRESHOLD);
      const truncatedWithMentions = ParseMentions(truncatedContent, fromMe);
      return (
        <>
          {ReplaceUrlsWithLinks(truncatedWithMentions)}
          <span className={`${fromMe ? "text-white" : "text-black"}`}>...</span>
        </>
      );
    }
  };
  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "0 Byte";
    const i = Number.parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
  };

  const handlePreview = (message) => {
    const modifiedFile = {
      name: message.fileName,
      extension: message.fileExtension,
      messageId: String(message.id),
      id: message.id,
    };
    setPreviewFile(modifiedFile);
  };

  //Delete Message
  const handleDeleteMessage = async (message) => {
    // SweetAlert2 confirmation dialog
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    // If the user cancels, exit the function
    if (!result.isConfirmed) return;

    try {
      const deleteMessageUrl =
        selectedConversationType === "group"
          ? `${baseURL}/GroupChat/Delete-Message/${message.id}`
          : `${baseURL}/Chats/Delete-Message/${message.id}`;
      const res = await callApi(deleteMessageUrl, "PUT");

      if (res.error) throw new Error(res.error);

      // Show success toast
      // toast.success("Message deleted successfully");

      // Fetch updated messages
      const updatedProperties = {
        isDeleted: 1,
      };

      dispatch(updateMessage({ messageId: message?.id, updatedProperties }));
      const updatedMessage = {
        ...updatedProperties,
        messageId: message?.id,
        senderId: currentUser,
      };
      await connection.invoke(
        "UpdateMessage",
        String(selectedConvo?.value),
        updatedMessage,
      );
    } catch (error) {
      // Show error toast
      toast.error(error.message || "Failed to delete the message");
    }
  };

  // Scroll to the replied message
  const scrollToRepliedMessage = (repliedMessageId) => {
    const repliedMessageElement = document.getElementById(repliedMessageId);

    if (repliedMessageElement) {
      // Scroll to the replied message
      repliedMessageElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Apply inline highlight style
      repliedMessageElement.style.backgroundColor = "#B0C4DE"; // Light blue background
      repliedMessageElement.style.borderRadius = "5px";
      repliedMessageElement.style.transition = "background-color 0.5s ease"; // Smooth transition

      // Remove highlight style after 1 second
      setTimeout(() => {
        repliedMessageElement.style.backgroundColor = ""; // Reset background color
      }, 1000); // 1 second delay
    } else {
      setTimeout(() => scrollToRepliedMessage(repliedMessageId), 500); // Retry after a delay
    }
  };

  // Get seen users information
  const getSeenUsers = () => {
    if (!message.seenUsers || !selectedConvoParticipants) return [];

    // Map seenUsers objects to participant objects with readAt timestamps
    return message.seenUsers
      .map((seenUser) => {
        const participant = selectedConvoParticipants.find(
          (p) => p.userId === seenUser.userId,
        );
        return participant
          ? {
              id: participant.userId,
              name: `${participant.firstName} ${participant.lastName}`,
              email: participant.email,
              readAt: seenUser.readAt, // Include the readAt timestamp
            }
          : null;
      })
      .filter((user) => user !== null); // Filter out any null values
  };

  // Toggle seen by popup
  const toggleSeenBy = () => {
    if (!showSeenBy) {
      determinePopupPosition();
    }
    setShowSeenBy(!showSeenBy);
    setShowEmojiPicker(false);
  };

  // Toggle emoji picker
  const toggleEmojiPicker = () => {
    if (!showEmojiPicker) {
      const buttonRect = emojiButtonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;
      setEmojiPickerPosition(getEmojiPickerPosition(buttonRect, fromMe));
      setShowEmojiPicker(true);
    } else {
      setShowEmojiPicker(false);
      setEmojiPickerPosition(null);
    }
    setShowSeenBy(false);
  };

  // Keep the picker glued to its button while the thread scrolls or the
  // window resizes. `capture: true` is needed because scroll events on the
  // messages container do not bubble to document.
  useEffect(() => {
    if (!showEmojiPicker) return undefined;

    const reposition = () => {
      const buttonRect = emojiButtonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;
      // Measure what is actually rendered rather than assuming a height: the
      // picker opens as the short quick-reaction strip, so positioning it
      // against the full picker height left it floating far above the
      // message it belongs to.
      const pickerHeight =
        emojiPickerRef.current?.getBoundingClientRect()?.height ||
        REACTION_PICKER_INITIAL_HEIGHT;
      setEmojiPickerPosition(
        getEmojiPickerPosition(buttonRect, fromMe, pickerHeight),
      );
    };

    // Runs once now that the portal is mounted, correcting the estimate the
    // open handler had to make before the picker existed.
    reposition();

    // Expanding the strip into the full picker changes its height, which no
    // scroll/resize event would report.
    let sizeObserver;
    if (typeof ResizeObserver !== "undefined" && emojiPickerRef.current) {
      sizeObserver = new ResizeObserver(reposition);
      sizeObserver.observe(emojiPickerRef.current);
    }

    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      sizeObserver?.disconnect();
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [showEmojiPicker, fromMe]);

  // Determine if popup should appear above or below
  const determinePopupPosition = () => {
    if (messageRef.current) {
      const rect = messageRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const minRequiredSpace = 350; // Minimum space needed for emoji picker

      if (spaceAbove < minRequiredSpace) {
        setPopupPosition("bottom");
      } else {
        setPopupPosition("top");
      }
    }
  };

  // Handle emoji click

  // Persists a reaction change that has ALREADY been painted optimistically by
  // the caller, then notifies other clients. Runs in the background: nothing
  // here blocks the UI. If the write fails the optimistic change is rolled
  // back to `previousReactions`; a SignalR failure is only logged, since by
  // then the reaction is already saved and rolling back would be wrong (same
  // best-effort approach useSendMessage takes after a successful send).
  const persistReaction = async ({
    isRemoving,
    emoji,
    updatedProperties,
    previousReactions,
  }) => {
    try {
      if (isRemoving) {
        const removeUrl =
          selectedConversationType === "group"
            ? `${baseURL}/GroupChat/RemoveReaction`
            : `${baseURL}/Chats/RemoveIndividualReaction`;
        await callApi(removeUrl, "delete", {
          messageId: message.id,
          userId: currentUser,
        });
      } else {
        const addUrl =
          selectedConversationType === "group"
            ? `${baseURL}/GroupChat/AddReaction`
            : `${baseURL}/Chats/AddIndividualReaction`;
        await callApi(addUrl, "post", {
          messageId: message.id,
          userId: currentUser,
          reaction: emoji,
        });
      }
    } catch (error) {
      console.error("Error updating reaction:", error);
      setReactions(previousReactions);
      dispatch(
        updateMessage({
          messageId: message?.id,
          updatedProperties: { reactions: previousReactions },
        }),
      );
      return;
    }

    try {
      if (connection) {
        await connection.invoke(
          "UpdateMessage",
          String(selectedConvo?.value),
          {
            ...updatedProperties,
            messageId: message?.id,
            senderId: currentUser,
          },
        );
      }
    } catch (error) {
      console.error("Reaction saved, but realtime notify failed:", error);
    }
  };

  // Handle emoji click
  //
  // The reaction is applied to local state + Redux synchronously so the pill
  // and the pop animation appear on the very next paint, and the network write
  // is fired afterwards without being awaited. Previously the AddReaction POST
  // was awaited *before* any of that ran, so the emoji picker stayed open and
  // nothing moved until the request came back — that was the perceived lag.
  const handleEmojiClick = (emojiData) => {
    const previousReactions = reactions;
    const updatedReactions = { ...reactions };

    // Clicking the emoji you already reacted with toggles it back off.
    const isRemoving = (updatedReactions[emojiData.emoji] || []).includes(
      currentUser,
    );

    if (isRemoving) {
      updatedReactions[emojiData.emoji] = updatedReactions[
        emojiData.emoji
      ].filter((userId) => userId !== currentUser);

      if (updatedReactions[emojiData.emoji].length === 0) {
        delete updatedReactions[emojiData.emoji];
      }
    } else {
      // Remove user from any other emoji reactions first (one reaction per user)
      Object.keys(updatedReactions).forEach((emoji) => {
        updatedReactions[emoji] = updatedReactions[emoji].filter(
          (userId) => userId !== currentUser,
        );

        // Remove the emoji entry if no users are left
        if (updatedReactions[emoji].length === 0) {
          delete updatedReactions[emoji];
        }
      });

      // Add user's new reaction
      if (!updatedReactions[emojiData.emoji]) {
        updatedReactions[emojiData.emoji] = [];
      }
      updatedReactions[emojiData.emoji].push(currentUser);
    }

    // --- Paint immediately ---
    setReactions(updatedReactions);
    setShowEmojiPicker(false);

    if (!isRemoving) {
      const popId = `${message.id}-${emojiData.emoji}-${Date.now()}`;
      setReactionPop({ id: popId, emoji: emojiData.emoji });
      if (reactionPopTimerRef.current) {
        clearTimeout(reactionPopTimerRef.current);
      }
      reactionPopTimerRef.current = setTimeout(() => {
        setReactionPop(null);
      }, 400);
    }

    const updatedProperties = { reactions: updatedReactions };
    dispatch(updateMessage({ messageId: message?.id, updatedProperties }));

    // --- Then persist, without blocking the UI ---
    persistReaction({
      isRemoving,
      emoji: emojiData.emoji,
      updatedProperties,
      previousReactions,
    });
  };

  const handleRemoveReaction = (emoji) => {
    const previousReactions = reactions;
    const updatedReactions = { ...reactions };

    // Remove user from this emoji's reactions
    updatedReactions[emoji] = (updatedReactions[emoji] || []).filter(
      (userId) => userId !== currentUser,
    );

    // Remove the emoji entry if no users are left
    if (updatedReactions[emoji].length === 0) {
      delete updatedReactions[emoji];
    }

    setReactions(updatedReactions);

    const updatedProperties = { reactions: updatedReactions };
    dispatch(updateMessage({ messageId: message?.id, updatedProperties }));

    persistReaction({
      isRemoving: true,
      emoji,
      updatedProperties,
      previousReactions,
    });
  };

  // handle resend message
  const handleResendMessage = async (msg) => {
    let formData;
    if (msg?.repliedMessage) {
      formData = {
        Content: msg?.content || "",
        File: msg.attachedFile || null,
        RepliedMessageId: msg?.repliedMessage?.id || null,
        ReplyingTo: msg?.repliedMessage,
      };
    } else {
      formData = {
        Content: msg?.content || "",
        File: msg.attachedFile || null,
      };
    }

    // Pass the ID of the failed message to the sendmessage function
    await sendmessage(formData, true, msg.id);
  };

  // Get user name for tooltip
  const getUserNameById = (userId) => {
    if (userId === currentUser) return "You";

    const participant = selectedConvoParticipants?.find(
      (p) => p.userId === userId,
    );
    return selectedConversationType === "simple"
      ? selectedConvo?.label
      : participant
        ? `${participant?.firstName} ${participant?.lastName}`
        : "Unknown User";
  };

  // Copy message to clipboard
  const handleCopyMessage = async (content) => {
    try {
      await copyRichText(content);
      toast.success("Message copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy message");
      console.error("Failed to copy: ", err);
    }
  };

  // Close popups when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (seenByRef.current && !seenByRef.current.contains(event.target)) {
        setShowSeenBy(false);
      }

      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update reactions when message changes
  useEffect(() => {
    if (message.reactions) {
      setReactions(message.reactions);
    }
  }, [message.reactions]);

  useEffect(() => {
    return () => {
      if (reactionPopTimerRef.current) {
        clearTimeout(reactionPopTimerRef.current);
      }
    };
  }, []);

  const [isNewMessage, setIsNewMessage] = useState(() => {
    // For group messages, check if current user is not in seenUsers array
    if (message.seenUsers && Array.isArray(message.seenUsers)) {
      const hasSeen = message.seenUsers.some(
        (seenUser) => seenUser.userId === currentUser,
      );
      return !hasSeen && !fromMe;
    }
    // Fallback to original logic for non-group messages
    return !message.isRead && !fromMe;
  });
  useEffect(() => {
    if (isNewMessage) {
      const timer = setTimeout(() => {
        setIsNewMessage(false);
      }, 5000); // Changed from 3000ms to 2000ms for 2-second timeout

      return () => clearTimeout(timer);
    }
  }, [isNewMessage]);

  if (loading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }, (_, index) => {
          const isFromMe = index % 2 === 0;
          return (
            <div
              key={index}
              className={`flex ${isFromMe ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`flex flex-col ${
                  isFromMe ? "items-end" : "items-start"
                } max-w-xs`}
              >
                <div
                  className={`h-10 w-48 rounded-2xl ${
                    isFromMe ? "bg-gray-300" : "bg-gray-200"
                  } animate-pulse`}
                ></div>
                <div className="h-3 w-16 mt-1 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Get seen users for this message
  const seenUsers = getSeenUsers();
  const allParticipantsSeen =
    selectedConvoParticipants &&
    message?.seenUsers &&
    message?.seenUsers?.length == selectedConvoParticipants?.length;

  return (
    <div
      className={`flex items-start ${
        fromMe ? "justify-end" : "justify-start"
      } mb-2 group`}
      id={message.id} // Add unique ID to the message container
      ref={messageRef}
    >
      <div
        className={`flex flex-col ${
          fromMe ? "items-end" : "items-start"
        } max-w-xs md:max-w-md`}
      >
        {/* Sender Name and Email (only for messages not from the current user) */}
        {!fromMe && message?.firstName && (
          <div className="flex items-center gap-1 mb-1">
            <p className="text-sm font-bold text-gray-700">
              {message?.firstName} {message?.lastName}
            </p>
          </div>
        )}

        {/* Message Bubble */}
        <div
          className={`px-4 py-3 drop-shadow-md ${
            fromMe
              ? "bg-sideNavbg text-white rounded-l-3xl  rounded-tr-3xl"
              : `bg-white text-black rounded-r-3xl ${
                  selectedConversationType === "simple"
                    ? "rounded-tl-3xl"
                    : "rounded-bl-3xl"
                }`
          } ${message?.shouldShake ? "animate-shake" : ""} ${
            isNewMessage
              ? "ring-2 ring-blue-400 ring-opacity-60 shadow-lg shadow-blue-200/50"
              : ""
          } ${
            isNewMessage ? "highlight-new-message" : ""
          } shadow-sm relative transition-all duration-300`}
          style={{
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
            wordBreak: "break-word",
          }}
          title={
            fromMe &&
            selectedConversationType === "simple" &&
            (message.isRead ? `Read ${formatReadTime(message.updatedAt)}` : "")
          }
        >
          {message?.isDeleted == 1 ? (
            <p className="text-gray-400 italic">
              This message has been deleted
            </p>
          ) : (
            <>
              {/* Reply Button (appears on hover) */}
              {!message?.isFailed && (
                <div
                  className={`absolute -top-5 ${
                    fromMe ? "right-0" : "left-0"
                  } flex space-x-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 bg-blue-50 px-2 py-1 rounded-full z-10 border-2 border-blue-400`}
                >
                  {fromMe && (
                    <>
                      {canEdit() && (
                        <button
                          className="bg-blue-500 text-white p-1 rounded-full shadow-md hover:bg-blue-600 transform hover:scale-105"
                          onClick={handleEditClick}
                          title="Edit message (available for 2 hours)"
                        >
                          <BiEdit className="text-lg" />
                        </button>
                      )}
                      <button
                        className="bg-gray-500 text-white p-1 rounded-full shadow-md hover:bg-gray-600 transform hover:scale-105"
                        onClick={() => handleDeleteMessage(message)}
                        title="Delete"
                      >
                        <BiTrash className="text-lg" />
                      </button>
                    </>
                  )}
                  {!fromMe && (
                    <button
                      ref={emojiButtonRef}
                      className="bg-gray-500 text-white p-1 rounded-full shadow-md hover:bg-gray-600 transform hover:scale-105"
                      onClick={toggleEmojiPicker}
                      title="Add Reaction"
                    >
                      <BiSmile className="text-lg" />
                    </button>
                  )}
                  {/*  copy button  */}
                  {message?.content && (
                    <button
                      className="bg-green-500 text-white p-1 rounded-full shadow-md hover:bg-green-600 transform hover:scale-105"
                      onClick={() => handleCopyMessage(message.content)}
                      title="Copy message"
                    >
                      <BiCopy className="text-lg" />
                    </button>
                  )}
                  <button
                    className="bg-blue-500 text-white p-1 rounded-full shadow-md hover:bg-blue-600 transform hover:scale-105"
                    onClick={() => setReplyingTo(message)}
                    title="Reply"
                  >
                    <BiReply className="text-lg" />
                  </button>
                </div>
              )}

              {fromMe && message?.isFailed && (
                <div
                  className={`absolute -top-5 ${
                    fromMe ? "right-0" : "left-0"
                  } flex space-x-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity duration-200 bg-blue-50 px-2 py-1 rounded-lg z-10 border-2 border-blue-400`}
                >
                  <button
                    className="bg-gray-500 text-white p-1 rounded-full shadow-md hover:bg-gray-600 transform hover:scale-105"
                    onClick={() => handleResendMessage(message)}
                    title="Resend"
                  >
                    <BiRevision className="text-lg" />
                  </button>
                </div>
              )}

              {/* Display replied message preview */}
              {message?.repliedMessageId && message?.repliedMessage && (
                <div
                  className={`mb-2 p-2  border-l-4 ${
                    fromMe
                      ? "bg-black bg-opacity-20 hover:bg-opacity-30 border-white"
                      : "bg-blue-50 hover:bg-blue-100 border-sideNavbg"
                  } rounded-lg  cursor-pointer  transition-colors`}
                  onClick={() =>
                    scrollToRepliedMessage(message?.repliedMessageId)
                  }
                >
                  <p
                    className={`text-sm font-bold ${
                      fromMe ? "text-gray-200" : "text-gray-500"
                    }`}
                  >
                    {currentUser === message?.repliedMessage?.senderId
                      ? "You"
                      : `${message?.repliedMessage?.firstName} ${message?.repliedMessage?.lastName}`}
                  </p>

                  <p
                    className={`text-sm ${
                      fromMe ? "text-gray-100" : "text-gray-500"
                    }`}
                  >
                    {message?.repliedMessage?.content
                      ? ParseMentions(richTextToPlain(message.repliedMessage.content).slice(0, 50) + (richTextToPlain(message.repliedMessage.content).length > 50 ? "..." : ""))
                      : message?.repliedMessage?.fileName}
                  </p>
                </div>
              )}

              {message?.fileName ? (
                <div className="flex flex-col">
                  <div className="flex items-center mb-2 bg-black bg-opacity-10 rounded-lg p-2.5 backdrop-blur-sm">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview(message);
                      }}
                      className="flex items-center flex-grow truncate group/file"
                      title={`Click to preview ${message.fileName}`}
                    >
                      <div
                        className={`p-2 rounded-md ${
                          fromMe
                            ? "bg-white bg-opacity-10 hover:bg-opacity-20"
                            : "bg-black bg-opacity-10 hover:bg-opacity-20"
                        } mr-3`}
                      >
                        <BiFile className="text-xl" />
                      </div>
                      <div className="flex items-center max-w-[200px]">
                        <div className="flex-grow overflow-hidden">
                          <div className="font-medium truncate group-hover/file:underline w-full block">
                            {message.fileName}
                          </div>
                          <div className="text-xs opacity-75">
                            {formatFileSize(message.fileSize)}
                          </div>
                        </div>
                      </div>
                    </a>

                    <button
                      title="Download"
                      onClick={() => downloadFile(message.id, message.fileName)}
                      disabled={isLoading}
                      className={`ml-2 p-2 rounded-full transition-colors duration-200 
                            ${
                              fromMe
                                ? "bg-white bg-opacity-10 hover:bg-opacity-20"
                                : "bg-black bg-opacity-10 hover:bg-opacity-20"
                            }`}
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <BiDownload className="text-xl" />
                      )}
                    </button>
                  </div>
                  {message.content && <div>{renderContent()}</div>}
                </div>
              ) : (
                renderContent()
              )}

              {richTextToPlain(message?.content).length > MESSAGE_LENGTH_THRESHOLD && (
                <button
                  className={`${
                    fromMe
                      ? "text-gray-200 hover:text-white"
                      : "text-sideNavbg hover:text-blue-600"
                  } text-sm font-medium flex items-center gap-1 mt-1  transition-colors`}
                  onClick={() => setIsExpanded(!isExpanded)}
                >
                  {isExpanded ? (
                    <>
                      Show less <BiChevronUp className="text-base" />
                    </>
                  ) : (
                    <>
                      Show more <BiChevronDown className="text-base" />
                    </>
                  )}
                </button>
              )}

              {/* Emoji Picker.

                  Rendered into <body> rather than here in the tree. The
                  message bubble above carries `drop-shadow-md`, and a CSS
                  filter both creates a stacking context (which trapped the
                  picker's z-index, letting later bubbles and this message's
                  own timestamp paint over it) and acts as the containing
                  block for fixed-position descendants (so `position: fixed`
                  alone would not have escaped it either). Portalling out is
                  the only reliable fix, and is what SoftwareCommentsCard.js
                  already does with this same picker. */}
              {showEmojiPicker &&
                emojiPickerPosition &&
                typeof document !== "undefined" &&
                createPortal(
                  <div
                    ref={emojiPickerRef}
                    style={{
                      position: "fixed",
                      top: `${emojiPickerPosition.top}px`,
                      left: `${emojiPickerPosition.left}px`,
                      zIndex: 2147483000,
                    }}
                  >
                    <EmojiPicker
                      theme={isDarkTheme(theme) ? Theme.DARK : Theme.LIGHT}
                      onEmojiClick={handleEmojiClick}
                      reactionsDefaultOpen={true}
                      skinTonePickerLocation={null}
                      emojiStyle={"google"}
                      height={REACTION_PICKER_BODY_HEIGHT}
                      width={REACTION_PICKER_WIDTH}
                      searchDisabled
                      skinTonesDisabled
                      previewConfig={{ showPreview: false }}
                    />
                  </div>,
                  document.body,
                )}
            </>
          )}
        </div>

        {/* Time and Read Status */}
        <div className="flex items-center mt-1 text-xs text-gray-500 relative">
          {/* Reactions display */}
          {Object.keys(reactions).length > 0 &&
            fromMe &&
            message?.isDeleted != 1 && (
              <div
                className={`flex flex-wrap gap-1 mr-2 ${
                  fromMe ? "justify-end" : "justify-start"
                }`}
              >
                {Object.entries(reactions).map(([emoji, userIds]) => (
                  <button
                    key={emoji}
                    className={`flex items-center bg-white hover:bg-gray-200 rounded-full px-2 py-0.5 text-sm ${
                      userIds.includes(currentUser)
                        ? "border-2 border-blue-400"
                        : ""
                    } `}
                    title={
                      userIds.length > 5
                        ? `${userIds
                            .slice(0, 5)
                            .map((id) => getUserNameById(id))
                            .join(", ")}\n+${userIds.length - 5} more...`
                        : userIds.map((id) => getUserNameById(id)).join(",\n")
                    }
                    disabled={fromMe}
                    onClick={() =>
                      userIds.includes(currentUser)
                        ? handleRemoveReaction(emoji)
                        : handleEmojiClick({ emoji })
                    }
                  >
                    <span
                      className={`mr-1 ${
                        reactionPop?.emoji === emoji
                          ? "animate-reaction-pop"
                          : ""
                      }`}
                    >
                      {emoji}
                    </span>
                    {selectedConversationType === "group" && (
                      <span className="text-gray-600">{userIds?.length}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          {/* Show edited text if message was edited (alongside other indicators) */}
          {message.isEdited && message?.isDeleted != 1 && (
            <p className="mr-1 text-gray-500 text-sm">{"(Edited)"}</p>
          )}

          <span>{formattedTime}</span>
          {message?.isDeleted != 1 && (
            <>
              {Object.keys(reactions).length > 0 && !fromMe && (
                <div
                  className={`flex flex-wrap gap-1 ml-2 ${
                    fromMe ? "justify-end" : "justify-start"
                  }`}
                >
                  {Object.entries(reactions).map(([emoji, userIds]) => (
                    <button
                      key={emoji}
                      className={`flex items-center bg-white hover:bg-gray-200 rounded-full px-2 py-0.5 text-sm ${
                        userIds.includes(currentUser)
                          ? "border-2 border-blue-400"
                          : ""
                      } `}
                      title={
                        userIds.length > 5
                          ? `${userIds
                              .slice(0, 5)
                              .map((id) => getUserNameById(id))
                              .join(", ")}\n+${userIds.length - 5} more...`
                          : userIds.map((id) => getUserNameById(id)).join(",\n")
                      }
                      disabled={fromMe}
                      onClick={() =>
                        userIds.includes(currentUser)
                          ? handleRemoveReaction(emoji)
                          : handleEmojiClick({ emoji })
                      }
                    >
                      <span
                        className={`mr-1 ${
                          reactionPop?.emoji === emoji
                            ? "animate-reaction-pop"
                            : ""
                        }`}
                      >
                        {emoji}
                      </span>
                      {selectedConversationType === "group" && (
                        <span className="text-gray-600">{userIds?.length}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Simple chat read status */}
              {fromMe && selectedConversationType === "simple" && (
                <>
                  {message.isFailed ? (
                    // Show error icon for failed messages
                    <BiError
                      title="Failed"
                      className="ml-1 text-red-500 text-lg"
                    />
                  ) : message.timestamp ? (
                    // Show clock icon for pending messages
                    <BiTime className="ml-1 text-gray-500 text-lg" />
                  ) : message.isRead ? (
                    // Show double check for read messages

                    <BiCheckDouble
                      className="ml-1 text-blue-500 text-lg"
                      title={`Read ${formatReadTime(message.updatedAt)}`}
                    />
                  ) : (
                    // Show single check for unread messages
                    <BiCheck className="ml-1 text-gray-500 text-lg" />
                  )}
                </>
              )}

              {/* Group chat read status with seen users popup */}
              {fromMe && selectedConversationType === "group" && (
                <div className="relative">
                  <button
                    className="flex items-center ml-1 focus:outline-none"
                    onClick={toggleSeenBy}
                    aria-label="View who has seen this message"
                    title="Seen by"
                  >
                    {message.isFailed ? (
                      // Show error icon for failed messages
                      <BiError
                        title="Failed"
                        className="text-red-500 text-lg"
                      />
                    ) : message.timestamp ? (
                      // Show clock icon for pending messages
                      <BiTime className="text-gray-500 text-lg" />
                    ) : allParticipantsSeen ? (
                      // Show double check for fully read messages
                      <BiCheckDouble className="text-blue-500 text-lg" />
                    ) : (
                      // Show single check for partially read messages
                      <div className="flex items-center">
                        <BiCheck className="text-gray-500 text-lg" />
                      </div>
                    )}
                  </button>

                  {/* Seen by popup */}
                  {showSeenBy && message?.seenUsers?.length > 0 && (
                    <div
                      ref={seenByRef}
                      className={`absolute ${
                        popupPosition === "top"
                          ? "bottom-full mb-1"
                          : "top-full mt-1"
                      } right-0 bg-white rounded-lg shadow-lg p-2 w-64 z-50`}
                    >
                      <div className="flex items-center justify-between border-b border-gray-200 pb-1 mb-2">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center">
                          <BiInfoCircle className="mr-1 text-blue-500" />
                          Seen by (
                          {
                            seenUsers?.filter((user) => user.id !== currentUser)
                              .length
                          }
                          )
                        </h4>
                        <button
                          onClick={() => setShowSeenBy(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <BiX className="text-base w-6 h-6" />
                        </button>
                      </div>

                      <div className="max-h-40 overflow-y-auto">
                        {seenUsers?.filter((user) => user.id !== currentUser)
                          .length > 0 ? (
                          <ul className="space-y-2">
                            {seenUsers
                              ?.filter((user) => user.id !== currentUser)
                              .map((user) => (
                                <li key={user.id} className="flex items-start">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center text-xs font-medium flex-shrink-0 mr-2">
                                    {user.name.charAt(0)}
                                  </div>
                                  <div className="overflow-hidden flex-1">
                                    <p className="text-sm font-medium text-gray-800 truncate">
                                      {user.name}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">
                                      {user.email}
                                    </p>
                                    {/* Read at time */}
                                    {user.readAt && (
                                      <p className="text-xs text-gray-400 mt-0.5">
                                        Read {formatReadTime(user.readAt)}
                                      </p>
                                    )}
                                  </div>
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-gray-500 text-center py-2">
                            No one has seen this message yet
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* File Preview Modal */}
      {previewFile && (
        <FilePreviewModal
          file={previewFile}
          onClose={() => setPreviewFile(null)}
          selectedConversationType={selectedConversationType}
        />
      )}
    </div>
  );
};

// Memoised because the parent rebuilds the whole message list on every change
// to the `messages` array. `updateMessage` in ChatSlice preserves the object
// identity of untouched messages, so with the narrowed selectors above a
// reaction (or edit, or read receipt) on one message now re-renders only that
// bubble instead of the entire thread. All props are either stable useState
// setters or values compared correctly by the default shallow check.
export default memo(Message);
